import type { QueryClient } from "@tanstack/react-query";
import type { AsyncTaskRegistry } from "@carepass/async-task";
import type { AuthSession, SessionChange, SessionStore } from "@carepass/auth";
import { parseApiDateTime } from "@carepass/auth";
import type { Schemas } from "../features/workspace/api.generated";
import type { AdminUiStore } from "./stores/admin-ui-store";

export type AdminSessionPhase = "anonymous" | "password-change" | "totp-enrollment" | "operations";
export type AdminSessionEndReason =
  | "logout"
  | "unauthorized"
  | "expired"
  | "password-changed"
  | "password-change-uncertain"
  | "totp-completed"
  | "totp-confirm-uncertain";

export interface AdminIdentity {
  readonly id: number;
  readonly loginId: string;
  readonly name: string;
}

export interface AdminSessionSnapshot {
  readonly authSession: AuthSession | null;
  readonly actor: AdminIdentity | null;
  readonly forcePasswordChange: boolean;
  readonly totpEnrollmentRequired: boolean;
  readonly phase: AdminSessionPhase;
  readonly sessionEpoch: number;
  readonly lastEndReason: AdminSessionEndReason | null;
}

export interface AdminSessionLifecycle {
  getSnapshot(): AdminSessionSnapshot;
  subscribe(listener: () => void): () => void;
  start(response: Schemas["AuthenticateAdminResponse"]): AdminSessionSnapshot;
  end(reason?: AdminSessionEndReason): void;
  clearEndReason(): void;
  createRequestController(): { readonly controller: AbortController; release(): void };
  createPreviewUrl(blob: Blob): string;
  revokePreviewUrl(url: string): void;
  isCurrentEpoch(sessionEpoch: number): boolean;
  dispose(): void;
}

interface UrlApi {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

export function createAdminSessionLifecycle(options: {
  readonly sessionStore: SessionStore;
  readonly queryClient: QueryClient;
  readonly uiStore: AdminUiStore;
  readonly asyncTasks: AsyncTaskRegistry;
  readonly urlApi?: UrlApi;
  readonly now?: () => number;
  readonly onSessionEnded?: (reason: AdminSessionEndReason) => void;
}): AdminSessionLifecycle {
  const listeners = new Set<() => void>();
  const controllers = new Set<AbortController>();
  const previewUrls = new Set<string>();
  const urlApi = options.urlApi ?? URL;
  const now = options.now ?? Date.now;
  const restoredSession = options.sessionStore.get();
  const restoredContext = restoredSession?.adminContext;
  if (restoredSession && !restoredContext) options.sessionStore.clear();
  const restoredPhase: AdminSessionPhase = restoredContext?.forcePasswordChange
    ? "password-change"
    : restoredContext?.totpEnrollmentRequired
      ? "totp-enrollment"
      : restoredContext
        ? "operations"
        : "anonymous";
  let changingStore = false;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let snapshot: AdminSessionSnapshot = restoredSession && restoredContext
    ? {
        authSession: restoredSession,
        actor: restoredContext.actor,
        forcePasswordChange: restoredContext.forcePasswordChange,
        totpEnrollmentRequired: restoredContext.totpEnrollmentRequired,
        phase: restoredPhase,
        sessionEpoch: 1,
        lastEndReason: null,
      }
    : {
        authSession: null,
        actor: null,
        forcePasswordChange: false,
        totpEnrollmentRequired: false,
        phase: "anonymous",
        sessionEpoch: 0,
        lastEndReason: null,
      };

  const notify = () => {
    for (const listener of listeners) listener();
  };

  const clearExpiryTimer = () => {
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
    expiryTimer = undefined;
  };

  const cleanupResources = (nextSessionEpoch: number) => {
    clearExpiryTimer();
    for (const controller of controllers) controller.abort();
    controllers.clear();
    void options.queryClient.cancelQueries();
    options.queryClient.clear();
    options.asyncTasks.clear("admin");
    for (const url of previewUrls) urlApi.revokeObjectURL(url);
    previewUrls.clear();
    options.uiStore.getState().resetForSession(nextSessionEpoch);
  };

  const end = (reason: AdminSessionEndReason = "logout", updateStore = true) => {
    const nextSessionEpoch = snapshot.sessionEpoch + 1;
    cleanupResources(nextSessionEpoch);
    snapshot = {
      authSession: null,
      actor: null,
      forcePasswordChange: false,
      totpEnrollmentRequired: false,
      phase: "anonymous",
      sessionEpoch: nextSessionEpoch,
      lastEndReason: reason,
    };
    if (updateStore) {
      changingStore = true;
      options.sessionStore.clear(reason === "expired" ? "expired" : "cleared");
      changingStore = false;
    }
    notify();
    options.onSessionEnded?.(reason);
  };

  const scheduleExpiry = (expiresAt: string) => {
    clearExpiryTimer();
    const tick = () => {
      const remaining = parseApiDateTime(expiresAt) - now();
      if (!Number.isFinite(remaining) || remaining <= 0) {
        end("expired");
        return;
      }
      expiryTimer = setTimeout(tick, Math.min(remaining, 2_147_483_647));
    };
    tick();
  };

  const unsubscribeStore = options.sessionStore.subscribe((change: SessionChange) => {
    if (changingStore || change.session) return;
    end(change.reason === "expired" ? "expired" : "logout", false);
  });

  if (snapshot.authSession) scheduleExpiry(snapshot.authSession.accessTokenExpiresAt);

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start(response) {
      const nextSessionEpoch = snapshot.sessionEpoch + 1;
      cleanupResources(nextSessionEpoch);
      const authSession: AuthSession = {
        scope: "admin",
        accessToken: response.accessToken,
        accessTokenExpiresAt: response.expiresAt,
        authorities: response.authorities,
        adminContext: {
          actor: response.adminUser,
          forcePasswordChange: response.forcePasswordChange,
          totpEnrollmentRequired: response.totpEnrollmentRequired,
        },
      };
      changingStore = true;
      options.sessionStore.set(authSession);
      changingStore = false;
      const phase: AdminSessionPhase = response.forcePasswordChange
        ? "password-change"
        : response.totpEnrollmentRequired
          ? "totp-enrollment"
          : "operations";
      snapshot = {
        authSession,
        actor: response.adminUser,
        forcePasswordChange: response.forcePasswordChange,
        totpEnrollmentRequired: response.totpEnrollmentRequired,
        phase,
        sessionEpoch: nextSessionEpoch,
        lastEndReason: null,
      };
      scheduleExpiry(response.expiresAt);
      notify();
      return snapshot;
    },
    end,
    clearEndReason() {
      if (snapshot.lastEndReason === null) return;
      snapshot = { ...snapshot, lastEndReason: null };
      notify();
    },
    createRequestController() {
      const controller = new AbortController();
      controllers.add(controller);
      return {
        controller,
        release: () => controllers.delete(controller),
      };
    },
    createPreviewUrl(blob) {
      const url = urlApi.createObjectURL(blob);
      previewUrls.add(url);
      return url;
    },
    revokePreviewUrl(url) {
      if (!previewUrls.delete(url)) return;
      urlApi.revokeObjectURL(url);
    },
    isCurrentEpoch: (sessionEpoch) => snapshot.sessionEpoch === sessionEpoch,
    dispose() {
      clearExpiryTimer();
      unsubscribeStore();
      for (const controller of controllers) controller.abort();
      controllers.clear();
      for (const url of previewUrls) urlApi.revokeObjectURL(url);
      previewUrls.clear();
      listeners.clear();
    },
  };
}
